import { CommonModule } from '@angular/common';
import { Component, Inject, NgModule, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import {
  ALL_ADVANCED_SECTIONS_ENABLED,
  DYNAMIC_COMPONENT_DATA_TOKEN,
  EmailBuilderModule,
  EmailContentData,
  VariableMenuItem,
} from '@galaxy/email-ui/email-builder';
import {
  emptyColors,
  emptyTopLogo,
} from '@galaxy/email-ui/email-builder/src/components/email-advanced-settings/empty-settings';
import { TranslateModule } from '@ngx-translate/core';
import { EmailBuilderDataService } from '@galaxy/email-ui/email-builder/src/services/email-builder-data.service';
import { AppNamespace, OwnerType } from '@vendasta/email-builder';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { EMPTY, Observable, of } from 'rxjs';

@Component({
  templateUrl: './email-builder-sandbox.component.html',
  styleUrls: ['./email-builder-sandbox.component.css'],
})
export class EmailBuilderSandboxComponent implements OnInit {
  isNew = true;

  templateId: string | undefined = undefined;
  templateData$: Observable<EmailContentData> = EMPTY;
  private dataForSaving: EmailContentData;
  private rawHtmlForSaving: string;
  htmlRendering = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dataSvc: EmailBuilderDataService,
    @Inject(DYNAMIC_COMPONENT_DATA_TOKEN) readonly dynamicComponents: VariableMenuItem[],
  ) {}

  ngOnInit(): void {
    this.templateId = this.route.snapshot.queryParams['templateId'];
    if (this.templateId) {
      this.isNew = false;
      this.templateData$ = this.dataSvc.loadTemplate$(
        OwnerType.OWNER_TYPE_PARTNER,
        'VUNI',
        AppNamespace.DEV_SANDBOX,
        this.templateId,
      );
    } else {
      this.templateId = new Date().toISOString();
      console.log('new template will be created with ID:', this.templateId);
      this.templateData$ = of({
        emailSetup: {
          topLogo: emptyTopLogo,
          colors: emptyColors,
          pageSetup: { collapsed: false },
          footer: { collapsed: false },
        },
        emailContentBlocks: [],
      } as EmailContentData);
    }
  }

  setHtmlRendering($event: boolean): void {
    this.htmlRendering = $event;
  }

  stageDataForSaving($event: EmailContentData): void {
    this.dataForSaving = $event;
  }

  stageRawHtmlForSaving($event: string): void {
    this.rawHtmlForSaving = $event;
  }

  save(): void {
    if (!this.dataForSaving) {
      throw new Error('No data has been staged for saving - try making a change to the email content');
    }
    this.isNew ? this.sendCreateRequest() : this.sendUpdateRequest();
  }

  private sendUpdateRequest(): void {
    this.dataSvc
      .updateExistingTemplate$(
        OwnerType.OWNER_TYPE_PARTNER,
        'VUNI',
        AppNamespace.DEV_SANDBOX,
        this.templateId,
        this.dataForSaving,
        this.rawHtmlForSaving,
      )
      .subscribe();
  }

  private sendCreateRequest(): void {
    this.dataSvc
      .createNewTemplate$(
        OwnerType.OWNER_TYPE_PARTNER,
        'VUNI',
        AppNamespace.DEV_SANDBOX,
        this.templateId,
        this.dataForSaving,
        this.rawHtmlForSaving,
      )
      .subscribe();
  }

  protected readonly ALL_ADVANCED_SECTIONS_ENABLED = ALL_ADVANCED_SECTIONS_ENABLED;
}

@NgModule({
  declarations: [EmailBuilderSandboxComponent],
  imports: [
    EmailBuilderModule,
    GalaxyPipesModule,
    CommonModule,
    GalaxyLoadingSpinnerModule,
    GalaxyAlertModule,
    GalaxyPageModule,
    MatButtonModule,
    TranslateModule,
  ],
  exports: [EmailBuilderSandboxComponent],
})
export class EmailBuilderSandboxComponentModule {}
